/**
 * Ansichtsmodell des Item-Shops
 * (Masterprompt Abschnitt 66, VISUAL_REFERENCE Abschnitte 46a und 46d).
 *
 * Hier steht Rechnung, keine Darstellung:
 *
 *   1. die drei Stufen eines Items als Kette von Punkten,
 *   2. die Wirkung einer Stufe in lesbaren Zeilen,
 *   3. was sich beim naechsten Ausbau aendert.
 *
 * Warum die Wirkung hier und nicht im Bauteil gebaut wird: Ein Item traegt
 * seine Wirkung als Zahlenfelder (maxHp, armor, ...). Daraus eine Zeile zu
 * machen ist eine Umrechnung mit Regeln - Prozent oder blanke Zahl, gerundet
 * oder nicht - und die soll geprueft sein.
 */

import { ITEM_SELL_RATIO, type ItemDefinition, type ItemEffects } from '@dueline/game-data';

/** Zustand einer Stufe in der Kette. */
export type StageState = 'owned' | 'next' | 'locked';

export interface StagePip {
  /** 1, 2 oder 3. */
  stage: number;
  state: StageState;
  /** Was diese Stufe kostet. 0, wenn sie schon gekauft ist. */
  cost: number;
}

/**
 * Die Stufenkette eines Items.
 *
 * @param stage  Bereits gekaufte Stufe. 0 = noch nicht im Besitz.
 * @param max    Hoechste Stufe dieses Items (das Totem hat nur eine).
 * @param kosten Kosten der Stufe mit diesem Index (0-basiert).
 */
export function buildStagePips(
  stage: number,
  max: number,
  kosten: (index: number) => number,
): StagePip[] {
  const erreicht = Math.max(0, Math.min(stage, max));
  const pips: StagePip[] = [];

  for (let i = 1; i <= max; i += 1) {
    const state: StageState = i <= erreicht ? 'owned' : i === erreicht + 1 ? 'next' : 'locked';
    pips.push({ stage: i, state, cost: state === 'owned' ? 0 : kosten(i - 1) });
  }

  return pips;
}

/** Eine Zeile in der Wirkungsliste. */
export interface EffectLine {
  /** Name der Wirkung, schon uebersetzt uebergeben. */
  label: string;
  /** Der Wert, fertig formatiert. */
  value: string;
}

/** Beschriftungen der Wirkungsfelder. Kommen aus der Oberflaeche. */
export type EffectLabels = Record<keyof ItemEffects, string>;

/**
 * Macht aus den Zahlenfeldern einer Stufe lesbare Zeilen.
 *
 * Die Reihenfolge ist fest und nicht die des Objekts: Leben zuerst, Tempo
 * zuletzt. So steht dieselbe Wirkung bei jedem Item an derselben Stelle.
 */
const FELD_REIHENFOLGE: (keyof ItemEffects)[] = [
  'maxHpRatio',
  'armor',
  'magicResistance',
  'attackDamageRatio',
  'maxManaRatio',
  'manaRegen',
  'hpRegenRatio',
  'healOnKillRatio',
  'cooldownReduction',
  'moveSpeedRatio',
  // Wirkung auf die eigene Line - steht bewusst unter den Heldenwerten.
  'sentCreepHpRatio',
  'incomeRatio',
  'sentCreepDamageRatio',
];

/** Felder, die als Prozent gelesen werden wollen. */
const ALS_PROZENT = new Set<keyof ItemEffects>([
  // Seit dem 28.09.2026 rechnen auch die Heldenwerte in Anteilen.
  'maxHpRatio',
  'attackDamageRatio',
  'maxManaRatio',
  'moveSpeedRatio',
  'hpRegenRatio',
  'healOnKillRatio',
  'cooldownReduction',
  'sentCreepHpRatio',
  'incomeRatio',
  'sentCreepDamageRatio',
]);

/** Eine Zahl englisch schreiben: Punkt als Trennzeichen, nur wenn noetig. */
function zahl(wert: number): string {
  return Number.isInteger(wert) ? String(wert) : wert.toFixed(1);
}

function formatiere(feld: keyof ItemEffects, wert: number): string {
  if (!ALS_PROZENT.has(feld)) return `+${zahl(wert)}`;

  // Die Verkuerzung der Abklingzeit ist ein Abzug, kein Zuschlag.
  const vorzeichen = feld === 'cooldownReduction' ? '-' : '+';
  return `${vorzeichen}${zahl(Math.round(wert * 1000) / 10)} %`;
}

export function buildEffectLines(effects: ItemEffects, labels: EffectLabels): EffectLine[] {
  const zeilen: EffectLine[] = [];

  for (const feld of FELD_REIHENFOLGE) {
    const wert = effects[feld];
    if (wert === undefined || wert === 0) continue;
    zeilen.push({ label: labels[feld], value: formatiere(feld, wert) });
  }

  return zeilen;
}

/**
 * Die Wirkung, die in der Detailspalte stehen soll.
 *
 * Gezeigt wird die Stufe, um die es gerade geht: Wer das Item noch nicht hat,
 * sieht Stufe 1; wer es hat, sieht die naechste Stufe - und wer es voll
 * ausgebaut hat, sieht die letzte. Die Stufen ersetzen einander, es wird also
 * nie etwas zusammengerechnet.
 */
export function stageToShow(item: ItemDefinition, stage: number): number {
  const max = item.stages.length;
  if (stage <= 0) return 1;
  return Math.min(stage + 1, max);
}

/** Verkaufserloes eines Items - dieselbe Regel wie auf dem Server. */
export function sellValue(investedGold: number): number {
  return Math.floor(investedGold * ITEM_SELL_RATIO);
}
